/**
 * Formatting utilities for the vendor data management feature.
 */

/**
 * Formats a number in Indian currency notation.
 *
 * Indian grouping rules:
 * - The last group before the decimal has exactly 3 digits
 * - All subsequent groups (moving left) have exactly 2 digits
 * - Groups are separated by commas
 * - Prefixed with ₹
 *
 * Examples:
 *   1000       → "₹1,000"
 *   100000     → "₹1,00,000"
 *   1250000    → "₹12,50,000"
 *   12345678.5 → "₹1,23,45,678.50"
 *   0          → "₹0.00"
 *   -5000      → "-₹5,000.00"
 *
 * @param amount - The number to format
 * @returns The formatted Indian currency string
 */
export function formatIndianCurrency(amount: number): string {
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount);

  // Split into integer and decimal parts, always show 2 decimal places
  const fixedStr = absoluteAmount.toFixed(2);
  const [integerPart, decimalPart] = fixedStr.split('.');

  // Apply Indian grouping to the integer part
  const groupedInteger = applyIndianGrouping(integerPart);

  const formatted = `₹${groupedInteger}.${decimalPart}`;
  return isNegative ? `-${formatted}` : formatted;
}

/**
 * Applies Indian number grouping to an integer string.
 * Last group is 3 digits, subsequent groups are 2 digits each.
 *
 * Examples:
 *   "1000"      → "1,000"
 *   "100000"    → "1,00,000"
 *   "1250000"   → "12,50,000"
 *   "123"       → "123"
 *   "12"        → "12"
 *   "0"         → "0"
 */
function applyIndianGrouping(integerStr: string): string {
  const len = integerStr.length;

  // No grouping needed for 3 or fewer digits
  if (len <= 3) {
    return integerStr;
  }

  // Take the last 3 digits as the first group (from the right)
  const lastThree = integerStr.slice(len - 3);
  const remaining = integerStr.slice(0, len - 3);

  // Group the remaining digits in pairs of 2 from the right
  const groups: string[] = [];
  for (let i = remaining.length; i > 0; i -= 2) {
    const start = Math.max(0, i - 2);
    groups.unshift(remaining.slice(start, i));
  }

  return groups.join(',') + ',' + lastThree;
}
